import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import moment from 'moment';

/* ── helpers ─────────────────────────────────────────────── */
const fmt = (d) => d ? moment(d).format('MMM D, YYYY') : null;
const arr = (a) => (Array.isArray(a) && a.length > 0) ? a : null;
const num = (v) => Number(v) || 0;
const sumColumn = (rows, key) => rows.reduce((sum, row) => sum + num(row[key]), 0);
const eventTypeLabel = (eventType, eventTypeOther) => {
	if (!eventType) return eventTypeOther || null;
	return eventType?.toLowerCase?.() === 'other' ? (eventTypeOther || eventType) : eventType;
};

const setupPreferenceLabel = (setupPreference, setupPreferenceOther) => {
	const preference = setupPreference?.[0] || null;
	if (!preference) return setupPreferenceOther || null;
	return preference?.toLowerCase?.() === 'other' ? (setupPreferenceOther || preference) : preference;
};

const sortByDate = (rows = []) => {
	return [...rows]
		.map((row, index) => ({ row, index }))
		.sort((a, b) => {
			const aHasDate = !!a.row?.date;
			const bHasDate = !!b.row?.date;

			if (!aHasDate && !bHasDate) return a.index - b.index;
			if (!aHasDate) return 1;
			if (!bHasDate) return -1;

			const aTime = moment(a.row.date).valueOf();
			const bTime = moment(b.row.date).valueOf();

			if (aTime === bTime) return a.index - b.index;
			return aTime - bTime;
		})
		.map(({ row }) => row);
};

const toMinutes = (time) => {
	const parsed = moment(time, ['h:mm A', 'HH:mm'], true);
	if (!parsed.isValid()) return Number.MAX_SAFE_INTEGER;
	return (parsed.hours() * 60) + parsed.minutes();
};

const getDurationMinutes = (item) => {
	const start = toMinutes(item.startTime);
	const end = toMinutes(item.endTime);
	if (start === Number.MAX_SAFE_INTEGER || end === Number.MAX_SAFE_INTEGER) {
		return Number.MAX_SAFE_INTEGER;
	}
	if (end >= start) return end - start;
	return (24 * 60 - start) + end;
};

const sortAgendaItems = (items = []) => {
	return [...items].sort((a, b) => {
		const startDiff = toMinutes(a.startTime) - toMinutes(b.startTime);
		if (startDiff !== 0) return startDiff;

		const durationDiff = getDurationMinutes(a) - getDurationMinutes(b);
		if (durationDiff !== 0) return durationDiff;

		const nameA = (a.sessionName || a.roomType || '').toLowerCase();
		const nameB = (b.sessionName || b.roomType || '').toLowerCase();
		return nameA.localeCompare(nameB);
	});
};

const sortAgendaEntriesByDate = (groupedAgenda, baseYear) => {
	return Object.entries(groupedAgenda)
		.map(([dateKey, items]) => ([dateKey, sortAgendaItems(items)]))
		.sort(([dateA], [dateB]) => {
			const parsedA = moment(`${dateA} ${baseYear}`, 'MMM D YYYY', true);
			const parsedB = moment(`${dateB} ${baseYear}`, 'MMM D YYYY', true);

			if (!parsedA.isValid() && !parsedB.isValid()) return dateA.localeCompare(dateB);
			if (!parsedA.isValid()) return 1;
			if (!parsedB.isValid()) return -1;
			return parsedA.valueOf() - parsedB.valueOf();
		});
};

const exportAgendaCSV = (agendaItems) => {
	const headers = ['Date', 'Session Name', 'Start Time', 'End Time', 'Room Type', 'Room Size', 'Attendees', 'Setup Preference', 'Notes', '24hr Hold'];
	const rows = agendaItems.map(item => {
		const dates = (item.selectedDates || []).join(' | ') || 'Unassigned';
		const setup = (item.setupPreference || [])
			.map(pref => String(pref).toLowerCase() === 'other' ? (item.setupPreferenceOther || 'other') : pref)
			.join(' | ');
		const roomSize = item.roomSize ? `${item.roomSize} ${item.roomSizeUnit || 'sq. ft.'}` : '';
		return [
			`"${dates}"`,
			`"${item.sessionName || ''}"`,
			`"${item.startTime || ''}"`,
			`"${item.endTime || ''}"`,
			`"${item.roomType || ''}"`,
			`"${roomSize}"`,
			item.attendees || 0,
			`"${setup}"`,
			`"${item.notes || ''}"`,
			item.require24Hour ? 'Yes' : 'No'
		].join(',');
	});
	const csv = [headers.join(','), ...rows].join('\n');
	const blob = new Blob([csv], { type: 'text/csv' });
	const url = URL.createObjectURL(blob);
	const a = document.createElement('a');
	a.href = url;
	a.download = 'meeting-spaces.csv';
	a.click();
	URL.revokeObjectURL(url);
};

const exportRoomBlockCSV = (dailyGrid) => {
	const headers = ['Date', 'King', 'Double', 'Suite', 'Staff', 'Total'];
	const rows = sortByDate(dailyGrid).map(row => {
		const total = num(row.king) + num(row.double) + num(row.suite) + num(row.staff);
		return [
			row.date ? `"${moment(row.date).format('ddd, MMM D')}"` : '',
			row.king || 0,
			row.double || 0,
			row.suite || 0,
			row.staff || 0,
			total
		].join(',');
	});
	const csv = [headers.join(','), ...rows].join('\n');
	const blob = new Blob([csv], { type: 'text/csv' });
	const url = URL.createObjectURL(blob);
	const a = document.createElement('a');
	a.href = url;
	a.download = 'room-block.csv';
	a.click();
	URL.revokeObjectURL(url);
};

const ReviewCard = ({ title, to, extraAction, children }) => (
	<div className="pr-review-card">
		<div className="pr-review-card-header">
			<h3 className="pr-review-card-title">{title}</h3>
			<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
				{extraAction}
				{to && (
					<Link to={to} className="pr-review-edit-btn">
						<i className="las la-pencil-alt" />
						Edit
					</Link>
				)}
			</div>
		</div>
		<div className="pr-review-card-body">{children}</div>
	</div>
);

const Field = ({ label, value }) => (value && value !== '—') ? (
	<div className="pr-review-field">
		<span className="pr-review-label">{label}</span>
		<span className="pr-review-value">{value}</span>
	</div>
) : null;

const Chips = ({ items }) => (
	<div className="pr-review-chips">
		{(items || []).map((item, i) => (
			<span key={i} className="pr-review-chip">
				{typeof item === 'string' ? item : (item.label || item.name || JSON.stringify(item))}
			</span>
		))}
	</div>
);

class ReviewSubmit extends Component {
	render() {
		const { request, orgId, requestId } = this.props;
		const base = `/${orgId}/proposal-requests/${requestId}/request`;

		const ed  = request?.eventDetails || {};
		const rb  = request?.roomBlock || {};
		const kc  = request?.keyConsiderations || {};
		const pi  = request?.plannerInfo || {};
		const ag  = request?.agencyInfo || {};
		const agendaItems        = request?.agendaItems || [];
		const customConcessions  = request?.concessions?.custom || [];
		const additionalServices = request?.additionalServices || [];
		const sortedDailyGrid    = sortByDate(rb.dailyGrid || []);
		const agendaBaseYear     = ed.arrivalDate ? moment(ed.arrivalDate).year() : moment().year();

		const statesList     = (ed.locations?.states || []).filter(Boolean);
		const regionsList    = (ed.locations?.regions || []).filter(Boolean);
		const citiesList     = (ed.locations?.cities || []).filter(Boolean);
		const countriesList  = (ed.locations?.countries || []).filter(Boolean);
		const preferredHotels = (ed.locations?.hotels || []).filter(Boolean);
		const plannerName     = [pi.firstName, pi.lastName].filter(Boolean).join(' ') || pi.contactName || null;
		const resolvedEventType = eventTypeLabel(ed.eventType, ed.eventTypeOther);

		/* Group agenda items by date */
		const groupedAgenda = {};
		agendaItems.forEach((item) => {
			const dates = item.selectedDates || [];
			if (dates.length === 0) {
				if (!groupedAgenda['Unassigned']) groupedAgenda['Unassigned'] = [];
				groupedAgenda['Unassigned'].push(item);
			} else {
				dates.forEach((d) => {
					if (!groupedAgenda[d]) groupedAgenda[d] = [];
					groupedAgenda[d].push(item);
				});
			}
		});
		const sortedGroupedAgendaEntries = sortAgendaEntriesByDate(groupedAgenda, agendaBaseYear);

		return (
			<div className="pr-review-grid">

				{/* ── Event Details ── */}
				<ReviewCard title="Event Details" to={`${base}/event`}>
					<Field label="Event Name"     value={ed.eventName} />
					<Field label="Event Type"     value={resolvedEventType} />
					<Field label="Arrival"        value={fmt(ed.arrivalDate)} />
					<Field label="Departure"      value={fmt(ed.departureDate)} />
					{arr(ed.alternateDates) && (
						<div className="pr-review-field">
							<span className="pr-review-label">Alt. Dates</span>
							<div className="pr-review-value" style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
								{ed.alternateDates.map((d, i) => (
									<span key={i}>{fmt(d.arrivalDate)} – {fmt(d.departureDate)}</span>
								))}
							</div>
						</div>
					)}
					<Field label="Peak Attendees" value={ed.expectedPeakAttendees > 0 ? String(ed.expectedPeakAttendees) : null} />
					<Field label="Max Room Rate"  value={ed.maxRoomRate ? `$${Number(ed.maxRoomRate).toLocaleString()} / night` : null} />
					<Field label="Star Rating"    value={ed.hotelStarRating ? `${ed.hotelStarRating} ★` : null} />
					<Field label="Desired Brands" value={ed.desiredBrands} />
					{statesList.length > 0 && (
						<div className="pr-review-field">
							<span className="pr-review-label">States</span>
							<Chips items={statesList} />
						</div>
					)}
					{regionsList.length > 0 && (
						<div className="pr-review-field">
							<span className="pr-review-label">Regions</span>
							<Chips items={regionsList} />
						</div>
					)}
					{citiesList.length > 0 && (
						<div className="pr-review-field">
							<span className="pr-review-label">Cities</span>
							<Chips items={citiesList} />
						</div>
					)}
					{countriesList.length > 0 && (
						<div className="pr-review-field">
							<span className="pr-review-label">Countries</span>
							<Chips items={countriesList} />
						</div>
					)}
					{preferredHotels.length > 0 && (
						<div className="pr-review-field">
							<span className="pr-review-label">Preferred Hotels</span>
							<Chips items={preferredHotels} />
						</div>
					)}
					{arr(ed.programDays) && (
						<div className="pr-review-field">
							<span className="pr-review-label">Program Days</span>
							<Chips items={ed.programDays} />
						</div>
					)}
					{ed.openToAltArrival && arr(ed.altArrivalDays) && (
						<div className="pr-review-field">
							<span className="pr-review-label">Alt. Arrival Days</span>
							<Chips items={ed.altArrivalDays} />
						</div>
					)}
					{ed.datePatternNotes && <Field label="Pattern Notes" value={ed.datePatternNotes} />}
					{ed.locations?.avoidSourcingNotes && <Field label="Avoid Sourcing" value={ed.locations.avoidSourcingNotes} />}
					{ed.locations?.locationNotes && <Field label="Additional Location" value={ed.locations.locationNotes} />}
				</ReviewCard>

				{/* ── Room Block ── */}
				{arr(sortedDailyGrid) ? (
					<ReviewCard
						title="Room Block"
						to={`${base}/rooms`}
						extraAction={
							<button
								type="button"
								className="pr-review-edit-btn"
								onClick={() => exportRoomBlockCSV(sortedDailyGrid)}
							>
								<i className="las la-download" />
								Export CSV
							</button>
						}
					>
						<div className="pr-review-rb-table">
							<div className="pr-review-rb-head">
								<span>Date</span>
								<span>King</span>
								<span>Dbl</span>
								<span>Suite</span>
								<span>Staff</span>
								<span>Total</span>
							</div>
							{sortedDailyGrid.map((row, i) => {
								const total = num(row.king) + num(row.double) + num(row.suite) + num(row.staff);
								return (
									<div key={i} className="pr-review-rb-row">
										<span>{row.date ? moment(row.date).format('ddd, MMM D') : `Night ${i + 1}`}</span>
										<span>{row.king || 0}</span>
										<span>{row.double || 0}</span>
										<span>{row.suite || 0}</span>
										<span>{row.staff || 0}</span>
										<span><strong>{total}</strong></span>
									</div>
								);
							})}
							{(() => {
								const totKing = sumColumn(sortedDailyGrid, 'king');
								const totDouble = sumColumn(sortedDailyGrid, 'double');
								const totSuite = sumColumn(sortedDailyGrid, 'suite');
								const totStaff = sumColumn(sortedDailyGrid, 'staff');
								const totAll = totKing + totDouble + totSuite + totStaff;
								return (
									<div className="pr-review-rb-row" style={{ borderTop: '2px solid #e5e7eb', background: '#f9fafb', fontWeight: 600 }}>
										<span style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>Totals</span>
										<span>{totKing}</span>
										<span>{totDouble}</span>
										<span>{totSuite}</span>
										<span>{totStaff}</span>
										<span><strong>{totAll}</strong></span>
									</div>
								);
							})()}
						</div>
						{rb.datesFlexible && (
							<p style={{ fontSize: 12, color: '#888', margin: '8px 0 0' }}>
								<i className="las la-check-circle" /> Dates are flexible
							</p>
						)}
					</ReviewCard>
				) : (
					<ReviewCard title="Room Block" to={`${base}/rooms`}>
						<span className="pr-review-empty">No room block defined.</span>
					</ReviewCard>
				)}

				{/* ── Meeting Space & Agenda ── */}
				<ReviewCard
					title="Meeting Space & Agenda"
					to={`${base}/agenda`}
					extraAction={agendaItems.length > 0 ? (
						<button
							type="button"
							className="pr-review-edit-btn"
							onClick={() => exportAgendaCSV(agendaItems)}
						>
							<i className="las la-download" />
							Export CSV
						</button>
					) : null}
				>
					{agendaItems.length === 0 ? (
						<span className="pr-review-empty">No agenda items added.</span>
					) : (
						sortedGroupedAgendaEntries.map(([date, items]) => (
							<div key={date} style={{ marginBottom: 12 }}>
								<div className="pr-review-date-group-label">{date}</div>
								{items.map((item, i) => (
									<div key={i} className="pr-review-agenda-row">
										<span className="pr-review-agenda-time">
											{item.startTime} – {item.endTime}
										</span>
										<span className="pr-review-agenda-type">{item.roomType}</span>
										{item.attendees > 0 && (
											<span className="pr-review-meta">
												<i className="las la-user" /> {item.attendees}
											</span>
										)}
										{item.roomSize && (
											<span className="pr-review-meta">
												<i className="las la-expand" /> {item.roomSize} {item.roomSizeUnit || 'sq. ft.'}
											</span>
										)}
										{arr(item.setupPreference) && (
											<span className="pr-review-meta">
												<i className="las la-th" /> {setupPreferenceLabel(item.setupPreference, item.setupPreferenceOther)}
											</span>
										)}
										{item.notes && (
											<span className="pr-review-meta pr-review-italic" title={item.notes}>
												<i className="las la-sticky-note" />
												<span className="pr-review-notes-text">{item.notes}</span>
											</span>
										)}
										{item.sessionName && (
											<span className="pr-review-meta pr-review-italic">{item.sessionName}</span>
										)}
										{item.require24Hour && (
											<span className="pr-review-badge">24hr</span>
										)}
									</div>
								))}
							</div>
						))
					)}
				</ReviewCard>

				{/* ── Concessions ── */}
				<ReviewCard title="Concessions" to={`${base}/concessions`}>
					{arr(customConcessions) ? (
						<Chips items={customConcessions} />
					) : (
						<span className="pr-review-empty">No concessions specified.</span>
					)}
				</ReviewCard>

				{/* ── Key Considerations ── */}
				<ReviewCard title="Key Considerations" to={`${base}/considerations`}>
					{kc.decisionFactors   && <Field label="Decision Factors"   value={kc.decisionFactors} />}
					{kc.eventHistory      && <Field label="Event History"       value={kc.eventHistory} />}
					{kc.reservationNotes  && <Field label="Reservation Notes"   value={kc.reservationNotes} />}
					{!kc.decisionFactors && !kc.eventHistory && !kc.reservationNotes && (
						<span className="pr-review-empty">No considerations noted.</span>
					)}
				</ReviewCard>

				{/* ── Additional Services ── */}
				<ReviewCard title="Additional Services" to={`${base}/services`}>
					{arr(additionalServices) ? (
						<Chips items={additionalServices} />
					) : (
						<span className="pr-review-empty">No additional services selected.</span>
					)}
				</ReviewCard>

				{/* ── Contract Signee ── */}
				<ReviewCard title="Contract Signee" to={`${base}/planner`}>
					<Field label="Name"            value={plannerName} />
					<Field label="Title"           value={pi.title} />
					<Field label="Company"         value={pi.companyName} />
					<Field label="Email"           value={pi.email} />
					<Field label="Phone"           value={pi.phone} />
					<Field label="Signer Location" value={pi.signerLocation} />
					<Field label="Due Date"        value={fmt(pi.responseDueDate)} />
					<Field label="Decision Date"   value={fmt(pi.decisionDate)} />
					{!plannerName && !pi.email && (
						<span className="pr-review-empty">No signee info provided.</span>
					)}
				</ReviewCard>

				{/* ── Agency (only if present) ── */}
				{(ag.agencyName || ag.iataNumber) && (
					<ReviewCard title="Agency" to={`${base}/agency`}>
						<Field label="Agency"         value={ag.agencyName} />
						<Field label="IATA"           value={ag.iataNumber} />
						<Field label="Commissionable" value={ag.isCommissionable ? 'Yes' : null} />
					</ReviewCard>
				)}

			</div>
		);
	}
}

export default ReviewSubmit;
